import type { TFunction } from 'i18next';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  useDroppable,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import { Plus, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDebounce } from '../hooks/useDebounce';
import { useQuotes, useUpdateQuoteStatus, type ConfidenceFilter } from '../api/hooks';
import {
  ALL_STATUSES,
  STATUS_STAGE,
  STATUS_TRANSITIONS,
  statusBadgeProps,
} from '@/lib/statusTransitions';
import type { QuoteListItem, QuoteStatus } from '../api/types';

function formatRelativeDate(dateStr: string, t: TFunction, locale: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return t('time.justNow');
  if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
  if (diffHours < 48) return t('time.yesterday');
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

function confidenceBadgeClass(score: number): string {
  if (score >= 80) return 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400';
  if (score >= 50)
    return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
  return 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400';
}

interface KanbanCardProps {
  quote: QuoteListItem;
  onClick: (id: string) => void;
  locale: string;
  t: TFunction;
  isDragging?: boolean;
}

function KanbanCardContent({
  quote,
  locale,
  t,
}: Readonly<{ quote: QuoteListItem; locale: string; t: TFunction }>) {
  return (
    <Card className="transition-colors hover:bg-muted/50">
      <CardContent className="p-3">
        <p className="font-medium">{quote.client_name || t('quote.untitled')}</p>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-sm font-semibold">&euro;{(quote.grand_total ?? 0).toFixed(2)}</span>
          <Badge variant="outline" className={confidenceBadgeClass(quote.confidence_score)}>
            {quote.confidence_score}%
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {formatRelativeDate(quote.created_at, t, locale)}
        </p>
      </CardContent>
    </Card>
  );
}

function DraggableKanbanCard({ quote, onClick, locale, t, isDragging }: Readonly<KanbanCardProps>) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: quote.id,
    data: { status: quote.status },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <button
      ref={setNodeRef}
      type="button"
      data-testid={`kanban-card-${quote.id}`}
      onClick={() => onClick(quote.id)}
      className={`w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        isDragging ? 'opacity-50' : ''
      }`}
      style={style}
      {...listeners}
      {...attributes}
    >
      <KanbanCardContent quote={quote} locale={locale} t={t} />
    </button>
  );
}

interface KanbanColumnProps {
  status: QuoteStatus;
  quotes: QuoteListItem[];
  onSelectQuote: (id: string) => void;
  locale: string;
  t: TFunction;
  activeId: string | null;
  overColumnId: string | null;
  sourceStatus: QuoteStatus | null;
}

function DroppableKanbanColumn({
  status,
  quotes,
  onSelectQuote,
  locale,
  t,
  activeId,
  overColumnId,
  sourceStatus,
}: Readonly<KanbanColumnProps>) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const badgeProps = statusBadgeProps(status);

  const isDragging = activeId !== null;
  const isHovering = isOver || overColumnId === status;
  const isValidTarget =
    sourceStatus !== null &&
    sourceStatus !== status &&
    STATUS_TRANSITIONS[sourceStatus].includes(status);

  let highlightClass = '';
  if (isDragging && isHovering && isValidTarget) {
    highlightClass = 'ring-2 ring-primary';
  }

  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${status}`}
      className={`flex min-w-[200px] flex-1 flex-col rounded-lg p-2 transition-shadow ${highlightClass}`}
    >
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold">{t(`quoteStatus.${status}`)}</h3>
        <Badge data-testid="column-count-badge" {...badgeProps}>
          {quotes.length}
        </Badge>
      </div>
      <ul className="list-none flex-1 space-y-2 overflow-y-auto">
        {quotes.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('quotes.emptyColumn')}
          </p>
        ) : (
          quotes.map((quote) => (
            <li key={quote.id}>
              <DraggableKanbanCard
                quote={quote}
                onClick={onSelectQuote}
                locale={locale}
                t={t}
                isDragging={activeId === quote.id}
              />
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

export interface KanbanBoardProps {
  onSelectQuote: (id: string) => void;
  onNewQuote: () => void;
}

type FilterKey = 'all' | 'high' | 'medium' | 'low';

const CONFIDENCE_FILTERS: Record<FilterKey, ConfidenceFilter | undefined> = {
  all: undefined,
  high: { min_confidence: 80 },
  medium: { min_confidence: 50, max_confidence: 79 },
  low: { max_confidence: 49 },
};

export function KanbanBoard({ onSelectQuote, onNewQuote }: Readonly<KanbanBoardProps>) {
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useKindeAuth();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const searchQuery = useDebounce(searchInput, 300);

  const updateStatus = useUpdateQuoteStatus(getAccessToken as () => Promise<string>);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { data, isLoading, isError, refetch } = useQuotes(
    getAccessToken as () => Promise<string>,
    'newest',
    CONFIDENCE_FILTERS[activeFilter],
    searchQuery || undefined,
    undefined,
    200,
  );

  const allQuotes = data?.pages.flatMap((page) => page.quotes) ?? [];

  const quotesByStatus: Record<QuoteStatus, QuoteListItem[]> = {
    draft: [],
    sent: [],
    accepted: [],
    rejected: [],
    invoiced: [],
    paid: [],
  };

  for (const quote of allQuotes) {
    quotesByStatus[quote.status].push(quote);
  }

  const activeQuote = activeId ? (allQuotes.find((q) => q.id === activeId) ?? null) : null;
  const sourceStatus = activeQuote?.status ?? null;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id as string | null;
    setOverColumnId(overId ?? null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setOverColumnId(null);

      if (!over) return;

      const draggedQuoteId = active.id as string;
      const fromStatus = active.data.current?.status as QuoteStatus;
      const toStatus = over.id as QuoteStatus;

      if (fromStatus === toStatus) return;
      if (!ALL_STATUSES.includes(toStatus)) return;

      const fromStage = STATUS_STAGE[fromStatus];
      const toStage = STATUS_STAGE[toStatus];
      const isBackward = toStage < fromStage;

      const doMutate = () => {
        updateStatus.mutate(
          { id: draggedQuoteId, status: toStatus },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['quotes'] });
              toast.success(t('quotes.statusUpdated', { status: t(`quoteStatus.${toStatus}`) }));
            },
            onError: () => {
              toast.error(t('quoteStatus.updateFailed'));
            },
          },
        );
      };

      if (isBackward) {
        toast(
          t('quotes.confirmBackward', {
            from: t(`quoteStatus.${fromStatus}`),
            to: t(`quoteStatus.${toStatus}`),
          }),
          {
            action: {
              label: t('quotes.confirmYes'),
              onClick: doMutate,
            },
          },
        );
      } else {
        doMutate();
      }
    },
    [t, updateStatus, queryClient],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setOverColumnId(null);
  }, []);

  return (
    <div className="flex h-full flex-col">
      {/* Header row: search + filters + new quote */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        {/* Search */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            data-testid="kanban-search-input"
            placeholder={t('quotes.searchPlaceholder')}
            aria-label={t('quotes.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 w-56 rounded-md border border-input bg-transparent pl-9 pr-9 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {searchInput && (
            <button
              type="button"
              data-testid="kanban-clear-search-btn"
              onClick={() => setSearchInput('')}
              aria-label={t('quotes.clearSearch')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Confidence filter chips */}
        <div className="flex gap-2">
          {(Object.keys(CONFIDENCE_FILTERS) as FilterKey[]).map((key) => (
            <Button
              key={key}
              variant={activeFilter === key ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setActiveFilter(key)}
              data-testid={`filter-chip-${key}`}
            >
              {t(`quotes.filter.${key}`)}
            </Button>
          ))}
        </div>

        {/* New Quote button */}
        <div className="ml-auto">
          <Button size="sm" onClick={onNewQuote} data-testid="new-quote-btn" className="gap-1.5">
            <Plus className="h-4 w-4" />
            {t('quotes.newQuote')}
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-1 gap-4 overflow-x-auto p-4">
          {ALL_STATUSES.map((status) => (
            <div key={status} className="flex min-w-[200px] flex-1 flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  data-testid="kanban-skeleton"
                  className="h-24 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">{t('error.loadQuotesFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
              {t('quotes.retry')}
            </Button>
          </div>
        </div>
      )}

      {/* Columns */}
      {!isLoading && !isError && (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div
            data-testid="kanban-columns-container"
            className="flex flex-1 gap-4 overflow-x-auto overflow-y-hidden p-4"
          >
            {ALL_STATUSES.map((status) => (
              <DroppableKanbanColumn
                key={status}
                status={status}
                quotes={quotesByStatus[status]}
                onSelectQuote={onSelectQuote}
                locale={i18n.language}
                t={t}
                activeId={activeId}
                overColumnId={overColumnId}
                sourceStatus={sourceStatus}
              />
            ))}
          </div>
          <DragOverlay>
            {activeQuote ? (
              <div className="w-[200px] opacity-90 shadow-lg" data-testid="drag-overlay">
                <KanbanCardContent quote={activeQuote} locale={i18n.language} t={t} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
